import { toast } from 'react-toastify';

import { callApi } from '../../../../../../apiCaller';
import { removeTrailingSlash, toastError } from '../../../../../../utils';
import { DEFAULT_PAGINATION_INIT, REGEX_URL, REGEX_URL_WWW } from '../../../../../../constants';

import { setIsWaiting, setRemoveLoading, setShowPopupDetail, setShowRemovePopup } from '../../../../../../actions/general';

export const SET_PRODUCT_LINKS = 'setAdminProductLinks';
export const ADD_PRODUCT_LINKS = 'addAdminProductLinks';
export const UPDATE_PRODUCT_LINK = 'updateAdminProductLink';
export const DELETE_PRODUCT_LINK = 'deleteAdminProductLink';

const API_SYSTEM_ADMIN_PRODUCT_LINK_DATA_LISTS = 'products/product-link/data-lists';
const API_SYSTEM_ADMIN_PRODUCT_LINK = 'products/product-link';
const API_SYSTEM_ADMIN_PRODUCT_LINK_DETAIL = 'products/product-link/:id';
const API_SYSTEM_ADMIN_PRODUCT_LINK_POPULATE_WAITING_SEAT = 'products/product-link/:productId/populate-link-waiting-seat';
const API_SYSTEM_ADMIN_PRODUCT_LINK_STATISTICS = 'products/product-link/:productId/statistics';

export const setAdminProductLinks = (productLinks) => {
     return {
          type: SET_PRODUCT_LINKS,
          payload: productLinks,
     };
};

export const addAdminProductLinks = (productLinks) => {
     return {
          type: ADD_PRODUCT_LINKS,
          payload: productLinks,
     };
};

export const updateAdminProductLink = (productLink) => {
     return {
          type: UPDATE_PRODUCT_LINK,
          payload: productLink,
     };
};

export const fetchAdminProductLinksRequest = (paginationPayload, setIsLoading) => (dispatch) => {
     const { filter, page, pageSize, sortBy, sortType } = paginationPayload;
     const filterQuery = Object.keys(filter).length ? `&filter=${encodeURIComponent(JSON.stringify(filter))}` : '';

     const endPoint = `${API_SYSTEM_ADMIN_PRODUCT_LINK_DATA_LISTS}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortType=${sortType}${filterQuery}`;

     setIsLoading(true);
     callApi(endPoint, 'GET')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               dispatch(addAdminProductLinks(response.data));
          })
          .finally(() => {
               setIsLoading(false);
          });
};

export const fetchAdminProductLinkRequest = (id, nextDispatch) => () => {
     const { setIsLoadingDetail, setProductLink } = nextDispatch;

     setIsLoadingDetail(true);

     callApi(API_SYSTEM_ADMIN_PRODUCT_LINK_DETAIL.replace(':id', id), 'GET')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               setProductLink(response.data);
          })
          .finally(() => {
               setIsLoadingDetail(false);
          });
};

export const addAdminProductLinksRequest = (values, nextDispatch) => (dispatch) => {
     const { productId, createdBy, links, productLinks } = values;

     const { setIsSubmitting, setIsLoading, setPagination, setProductLinkResults, setCurrentStepPopup, setStatistics } = nextDispatch;

     const newLinks = [];
     const linkErrors = [];
     const linksAlreadyExisted = [];

     for (let i = 0; i < links.length; i++) {
          const link = removeTrailingSlash(links[i]).replace(REGEX_URL_WWW, '');
          if (!link) continue;

          const validLink = REGEX_URL.test(String(link));
          if (!validLink) {
               linkErrors.push(link);
               continue;
          }

          // Link already exist
          const linkAlreadyExist =
               newLinks.some((linkItem) => linkItem === link) ||
               linksAlreadyExisted.some((linkExistItem) => linkExistItem === link) ||
               productLinks.some((productLink) => removeTrailingSlash(productLink.originalLink).replace(REGEX_URL_WWW, '') === link);

          if (linkAlreadyExist) {
               linksAlreadyExisted.push(link);
               continue;
          }

          newLinks.push(link);
     }

     if (!newLinks.length) {
          setProductLinkResults((prevResults) => {
               return {
                    ...prevResults,
                    failed: {
                         ...prevResults.failed,
                         items: linkErrors,
                    },
                    alreadyExisted: {
                         ...prevResults.alreadyExisted,
                         items: linksAlreadyExisted,
                    },
               };
          });

          setCurrentStepPopup();
          return;
     }

     const payload = {
          productId,
          createdBy,
          links: newLinks,
     };

     setIsSubmitting(true);

     callApi(API_SYSTEM_ADMIN_PRODUCT_LINK, 'POST', payload).then((response) => {
          if (response.status !== 200) {
               setIsSubmitting(false);
               toastError(response);
               return;
          }

          const { successfully: successfullyItems, failed: failedItems, alreadyExisted: alreadyExistedItems } = response.data.result;

          setProductLinkResults((prevResults) => ({
               ...prevResults,
               ...(successfullyItems.length && {
                    successfully: {
                         ...prevResults.successfully,
                         items: successfullyItems,
                    },
               }),
               ...((linkErrors.length || failedItems.length) && {
                    failed: {
                         ...prevResults.failed,
                         items: [...linkErrors, ...failedItems],
                    },
               }),
               ...((linksAlreadyExisted.length || alreadyExistedItems.length) && {
                    alreadyExisted: {
                         ...prevResults.alreadyExisted,
                         items: [...linksAlreadyExisted, ...alreadyExistedItems],
                    },
               }),
          }));

          setIsSubmitting(false);
          setCurrentStepPopup();

          // Reset filter table
          const paginationPayload = {
               ...DEFAULT_PAGINATION_INIT,
          };
          setPagination((currentPagination) => {
               paginationPayload.pageSize = currentPagination.pageSize;
               return paginationPayload;
          });

          dispatch(fetchAdminLinksStatistics(productId, setStatistics));
          dispatch(fetchAdminProductLinksRequest(paginationPayload, setIsLoading));
     });
};

export const updateAdminProductLinkRequest = (id, values, nextDispatch) => (dispatch) => {
     const { setSubmitting } = nextDispatch;

     callApi(API_SYSTEM_ADMIN_PRODUCT_LINK_DETAIL.replace(':id', id), 'PATCH', values)
          .then((response) => {
               setSubmitting(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const dataRes = response.data;

               const payloadAction = {
                    ...values,
                    id,
                    originalLink: dataRes.originalLink,
               };

               dispatch(updateAdminProductLink(payloadAction));
               toast.success('Update successful');
          })
          .finally(() => {
               dispatch(setShowPopupDetail(null));
          });
};

export const deleteAdminProductLinkRequest = (id) => async (dispatch) => {
     dispatch(setRemoveLoading(true));
     return await callApi(API_SYSTEM_ADMIN_PRODUCT_LINK_DETAIL.replace(':id', id), 'DELETE')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               toast.success('Delete successfully');
          })
          .finally(() => {
               dispatch(setRemoveLoading(false));
               dispatch(setShowRemovePopup(null));
          });
};

export const populateLinkWaitingSeat = (productId, nextDispatch) => (dispatch) => {
     const { setIsLoading, setPagination, setStatistics } = nextDispatch;

     dispatch(setIsWaiting(true));

     callApi(API_SYSTEM_ADMIN_PRODUCT_LINK_POPULATE_WAITING_SEAT.replace(':productId', productId), 'PATCH')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               // Reset filter table
               const paginationPayload = {
                    ...DEFAULT_PAGINATION_INIT,
               };
               setPagination((currentPagination) => {
                    paginationPayload.pageSize = currentPagination.pageSize;

                    return paginationPayload;
               });

               if (response.data.numberHasPopulate) {
                    dispatch(fetchAdminProductLinksRequest(paginationPayload, setIsLoading));
                    toast.success('Populate links to waiting seat successful');
               } else {
                    toast.success('No seats waiting to populate');
               }
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
               dispatch(fetchAdminLinksStatistics(productId, setStatistics));
          });
};

export const fetchAdminLinksStatistics = (productId, setStatistics) => () => {
     callApi(API_SYSTEM_ADMIN_PRODUCT_LINK_STATISTICS.replace(':productId', productId), 'GET').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          setStatistics(response.data);
     });
};
